// The scheduling request's Reschedule and Cancel dialogs (lane B5 S11, screens-walks §2.9): the D0 Dialog presets, his
// verbs, a required Reason, and the server's refusal kept inside the dialog with what was entered. Reschedule moves the
// booked meeting ("Updates modify the same Outlook event.") and shows Original → Revised as the time is chosen; a
// conflict the policy asks a reason for is booked only once the Coordinator ticks *Conflict* (the reason is already
// given); a double-booked room, an out-of-order loop and an unknown time zone are refused whatever is ticked.
// The availability grid and its slots live on the scheduling pages (DC-34, ScheduleSection); beside the Workbench the new
// time is typed in the event's time zone. The pages' Schedule, Reschedule, Request and Cancel dialogs follow (S12).

import { useState } from "react";
import type { MeetingOut, SchedulingOpenOutput } from "@/api/tools";
import { schedulingCancel, schedulingReschedule } from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import {
  ACCESS_LABELS, ACTION_LABELS, CheckTile, DIFF_LABELS, Diff, Dialog, Field, ITINERARY_LABELS, Input, KeyValue, Lines, Person,
  SCHEDULING_LABELS, Segmented, Stack, TextArea, formatDateTime,
} from "@/mc";
import { RESCHEDULE_REQUEST_LABELS } from "./labels";

const REASON_MIN = 3;

/** "YYYY-MM-DDTHH:mm" of an instant on the wall clock of `timeZone` (the form a datetime-local input carries). */
export function wallTime(at: string, timeZone: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(new Date(at)).map((p) => [p.type, p.value]),
  );
  return `${parts["year"]}-${parts["month"]}-${parts["day"]}T${parts["hour"]}:${parts["minute"]}`;
}

/** The instant a wall-clock time in `timeZone` names, as ISO (two passes settle a daylight-saving edge). */
export function zonedInstant(wall: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(wall);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number) as [number, number, number, number, number, number];
  const target = Date.UTC(y, mo - 1, d, h, mi);
  let guess = target;
  for (let pass = 0; pass < 2; pass += 1) {
    const seen = wallTime(new Date(guess).toISOString(), timeZone);
    const [sy, smo, sd, sh, smi] = seen.split(/[-T:]/).map(Number) as [number, number, number, number, number];
    guess += target - Date.UTC(sy, smo - 1, sd, sh, smi);
  }
  return new Date(guess).toISOString();
}

interface DialogBase {
  request: SchedulingOpenOutput;
  onClose: () => void;
  onDone: () => void;
}

export function RescheduleDialog({ request, meeting, onClose, onDone }: DialogBase & { meeting: MeetingOut }) {
  const tz = meeting.event_tz || request.event_tz;
  const [when, setWhen] = useState(meeting.starts_at ? wallTime(meeting.starts_at, tz) : "");
  const [reason, setReason] = useState("");
  const [accept, setAccept] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const [conflicted, setConflicted] = useState(false);
  const write = useWrite(schedulingReschedule, [["scheduling.workbench"], ["scheduling.open", request.scheduling_request_id]]);
  const start = zonedInstant(when, tz);
  const moved = start !== null && meeting.starts_at !== null && meeting.starts_at !== undefined && Date.parse(start) !== Date.parse(meeting.starts_at);
  const ready = moved && reason.trim().length >= REASON_MIN;

  const submit = async () => {
    if (!start) return;
    setRefused(null);
    try {
      await write.mutateAsync({
        scheduling_request_id: request.scheduling_request_id,
        expected_version: request.version,
        meeting_event_id: meeting.meeting_event_id,
        start,
        accept_conflicts: accept,
        reason: reason.trim(),
      });
      onDone();
    } catch (error) {
      setRefused(errorText(error));
      setConflicted(true);
    }
  };

  return (
    <Dialog
      preset="reschedule"
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      hint={SCHEDULING_LABELS.sameEvent}
      primary={{
        label: ACTION_LABELS.reschedule,
        onClick: () => void submit(),
        busy: write.isPending,
        blocked: ready ? undefined : { reason: `${ITINERARY_LABELS.when} · ${DIFF_LABELS.reason}` },
        hs: "sched.reschedule.submit",
      }}
      
    >
      <Stack gap={12}>
        <Diff
          original={meeting.starts_at ? formatDateTime(meeting.starts_at, tz) : "—"}
          revised={start ? formatDateTime(start, tz) : "—"}
        />
        <Field label={`${ITINERARY_LABELS.when} · ${tz}`} required>
          <Input type="datetime-local" value={when} onChange={(event) => setWhen(event.target.value)}  />
        </Field>
        <Field label={DIFF_LABELS.reason} required>
          <TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500}  />
        </Field>
        {conflicted ? (
          <CheckTile checked={accept} onChange={setAccept} >{SCHEDULING_LABELS.conflict}</CheckTile>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CancelDialog({ request, onClose, onDone }: DialogBase) {
  const [reason, setReason] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const write = useWrite(schedulingCancel, [["scheduling.workbench"], ["scheduling.open", request.scheduling_request_id]]);
  const told = [...new Set(request.meetings.filter((m) => m.state !== "cancelled").flatMap((m) => m.attendees.map((a) => a.name)))];
  const ready = reason.trim().length >= REASON_MIN;

  const submit = async () => {
    setRefused(null);
    try {
      await write.mutateAsync({ scheduling_request_id: request.scheduling_request_id, expected_version: request.version, reason: reason.trim() });
      onDone();
    } catch (error) {
      setRefused(errorText(error));
    }
  };

  return (
    <Dialog
      preset="cancel"
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      primary={{
        label: ACTION_LABELS.cancel,
        onClick: () => void submit(),
        busy: write.isPending,
        blocked: ready ? undefined : { reason: DIFF_LABELS.reason },
        hs: "sched.cancel.submit",
      }}
      
    >
      <Stack gap={12}>
        <Field label={DIFF_LABELS.reason} required>
          <TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500}  />
        </Field>
        {told.length > 0 ? <Lines items={told} plain sm /> : null}
      </Stack>
    </Dialog>
  );
}

// ---- the scheduling pages' dialogs (lane B5 S12, screens-walks §2.9) --------------------------------------------------------
// The page's card hands each dialog the write it makes; the dialog keeps what was entered and the server's refusal (his
// rule sentence) inside itself, and closes only when the write is recorded.

function useSubmit(run: () => Promise<void>, onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setRefused(null);
    try {
      await run();
      onDone();
    } catch (error) {
      setRefused(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  return { busy, refused, submit };
}

export interface FormatChoice {
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}

/** Schedule (S-6): the chosen slot with its zone, the format, the attendees; primary *Schedule*. A18 ("Conflicts and time
 *  zones are validated."): a slot that breaks a required attendee's calendar is scheduled only once the Coordinator ticks
 *  *Conflict* and gives the Reason, which the booking's receipt keeps. */
export function ScheduleDialog({ when, attendees, format, fixedFormat, conflicted = false, note, onSchedule, onClose, onDone }: {
  when: string;
  /** One more row under When (the intake call: *HM availability* and the HM's windows as his line, DC-231). */
  note?: readonly [string, string] | undefined;
  attendees: readonly { key: string; name: string; sub?: string | undefined }[];
  format?: FormatChoice | undefined;
  fixedFormat?: string | null | undefined;
  conflicted?: boolean | undefined;
  onSchedule: (reason?: string) => Promise<void>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [accept, setAccept] = useState(false);
  const { busy, refused, submit } = useSubmit(() => onSchedule(conflicted ? reason.trim() : undefined), onDone);
  const ready = !conflicted || (accept && reason.trim().length >= REASON_MIN);
  return (
    <Dialog
      preset="schedule"
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      primary={{
        label: ACTION_LABELS.schedule,
        onClick: () => void submit(),
        busy,
        blocked: ready ? undefined : { reason: `${SCHEDULING_LABELS.conflict} · ${DIFF_LABELS.reason}` },
        hs: "sched.schedule.submit",
      }}
      
    >
      <Stack gap={12}>
        <KeyValue cols={1} items={[[ITINERARY_LABELS.when, fixedFormat ? `${when} · ${fixedFormat}` : when], ...(note ? [note] : [])]} />
        {format ? <Segmented options={format.options} value={format.value} onChange={format.onChange} /> : null}
        {attendees.length ? (
          <Stack gap={8}>
            {attendees.map((who) => <Person key={who.key} name={who.name} sub={who.sub} hueKey={who.key} />)}
          </Stack>
        ) : null}
        {conflicted ? (
          <>
            <CheckTile checked={accept} onChange={setAccept} >{SCHEDULING_LABELS.conflict}</CheckTile>
            <Field label={DIFF_LABELS.reason} required>
              <TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500}  />
            </Field>
          </>
        ) : null}
      </Stack>
    </Dialog>
  );
}

/** Reschedule from the page (S-9): Original → Revised as the new slot reads, a required Reason; "Updates modify the same
 *  Outlook event." A18: the conflicts are checked again at the new time, so a slot that breaks a calendar (or a refusal
 *  that names one) shows *Conflict*, and the move goes through only once it is ticked (the Reason is already given). */
export function SlotRescheduleDialog({ original, revised, conflicted = false, onReschedule, onClose, onDone }: {
  original: string;
  revised: string;
  conflicted?: boolean | undefined;
  onReschedule: (reason: string, acceptConflicts: boolean) => Promise<void>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [accept, setAccept] = useState(false);
  const { busy, refused, submit } = useSubmit(() => onReschedule(reason.trim(), accept), onDone);
  const asked = conflicted || refused !== null;
  return (
    <Dialog
      preset="reschedule"
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      hint={SCHEDULING_LABELS.sameEvent}
      primary={{
        label: ACTION_LABELS.reschedule,
        onClick: () => void submit(),
        busy,
        blocked: reason.trim().length >= REASON_MIN ? undefined : { reason: DIFF_LABELS.reason },
        hs: "sched.reschedule.submit",
      }}
      
    >
      <Stack gap={12}>
        <Diff original={original} revised={revised} />
        <Field label={DIFF_LABELS.reason} required>
          <TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500}  />
        </Field>
        {asked ? (
          <CheckTile checked={accept} onChange={setAccept} >{SCHEDULING_LABELS.conflict}</CheckTile>
        ) : null}
      </Stack>
    </Dialog>
  );
}

/** Request mode (S-9, §2.8): the dialog titled *Reschedule request* (DC-241), Reason only; primary *Request* (the
 *  matrix word). */
export function RequestDialog({ onRequest, onClose, onDone }: { onRequest: (reason: string) => Promise<void>; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const { busy, refused, submit } = useSubmit(() => onRequest(reason.trim()), onDone);
  return (
    <Dialog
      title={RESCHEDULE_REQUEST_LABELS.rescheduleRequest}
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      primary={{
        label: ACCESS_LABELS.Request,
        onClick: () => void submit(),
        busy,
        blocked: reason.trim().length >= REASON_MIN ? undefined : { reason: DIFF_LABELS.reason },
        hs: "sched.request.submit",
      }}
      
    >
      <Field label={DIFF_LABELS.reason} required>
        <TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500}  />
      </Field>
    </Dialog>
  );
}

/** Cancel from the page: a required Reason and the attendees who will be told. */
export function ReasonCancelDialog({ told, onCancel, onClose, onDone }: {
  told: readonly string[];
  onCancel: (reason: string) => Promise<void>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const { busy, refused, submit } = useSubmit(() => onCancel(reason.trim()), onDone);
  return (
    <Dialog
      preset="cancel"
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      primary={{
        label: ACTION_LABELS.cancel,
        onClick: () => void submit(),
        busy,
        blocked: reason.trim().length >= REASON_MIN ? undefined : { reason: DIFF_LABELS.reason },
        hs: "sched.cancel.submit",
      }}
      
    >
      <Stack gap={12}>
        <Field label={DIFF_LABELS.reason} required>
          <TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500}  />
        </Field>
        {told.length > 0 ? <Lines items={told} plain sm /> : null}
      </Stack>
    </Dialog>
  );
}
